import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Radio,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import { Check as CheckIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Workaround for JSX typing issues with Stripe React components
const ElementsAny = Elements as unknown as React.ComponentType<any>;
const CardElementAny = CardElement as unknown as React.ComponentType<any>;
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import { useNavigate } from 'react-router-dom';

interface Plan {
  _id: string;
  name: string;
  description: string;
  monthlyFee: number;
  annualFee: number;
  billingPeriod: 'monthly' | 'annual' | 'both' | 'hybrid';
  features: string[];
  isActive: boolean;
  sortOrder: number;
  trialDays?: number;
  monthlyPrice?: {
    id: string;
    amount: number;
    currency: string;
  };
  annualPrice?: {
    id: string;
    amount: number;
    currency: string;
  };
  stripeProductId: string; // Added for Stripe product ID
  stripeMonthlyPriceId?: string;
  stripeAnnualPriceId?: string;
  seatsBased?: boolean;
  seatPrice?: number;
  metadata?: Record<string, string>;
  
  // Jira-style pricing fields
  creatorLimit?: number; // Max number of creators (null = unlimited)
  contributorLimit?: number; // Max number of contributors (null = unlimited)
  storageLimit?: number; // Storage limit in GB (null = unlimited)
  automationRuleRuns?: number; // Monthly automation rule runs (null = unlimited)
  supportLevel?: 'community' | 'business_hours' | 'premium_24_7'; // Support level
  includesAdvancedFeatures?: boolean; // Admin insights, release tracks, etc.
  includesIPAllowlisting?: boolean; // IP allowlisting feature
  includesAtlassianIntelligence?: boolean; // AI features
  trialEligible?: boolean;
  trialStatus?: 'not_available' | 'eligible' | 'used';
}

interface AgencySubscription {
  id: string;
  status: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  trialEnd?: number;
  items: Array<{
    priceId: string;
    productId: string;
    interval: string;
    amount: number;
    planName?: string;
  }>;
}

const isSubscriptionInTrial = (subscription?: AgencySubscription | null) => {
  if (!subscription) return false;
  if (subscription.status === 'trialing') return true;
  if (subscription.trialEnd && !isNaN(subscription.trialEnd)) {
    return subscription.trialEnd * 1000 > Date.now();
  }
  return false;
};

const PaymentMethodForm: React.FC<{ agencyId: string; onSuccess?: () => void }> = ({ agencyId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      // 1. Get client secret from backend
      const { data } = await api.post(`/billing/${agencyId}/create-setup-intent`);
      const clientSecret = data.clientSecret;
      // 2. Confirm card setup
      if (!stripe || !elements) throw new Error('Stripe not loaded');
      const cardElement = elements.getElement(CardElement as any) as any;
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement as any,
        },
      });
      if (result.error) {
        setError(result.error.message || 'Failed to update payment method.');
      } else {
        // Extract payment method ID - it can be a string or an object
        const paymentMethod = result.setupIntent.payment_method;
        const paymentMethodId = typeof paymentMethod === 'string' 
          ? paymentMethod 
          : (paymentMethod as any)?.id || paymentMethod;
        
        if (!paymentMethodId) {
          setError('Failed to get payment method ID from setup intent.');
          return;
        }

        console.log('[PaymentMethodForm] Payment method ID:', paymentMethodId);
        
        // Set as default payment method on backend
        try {
          const response = await api.post(`/billing/${agencyId}/payment-method/default`, { paymentMethodId });
          console.log('[PaymentMethodForm] Payment method set as default:', response.data);
        } catch (setErr: any) {
          console.error('[PaymentMethodForm] Failed to set default payment method:', setErr);
          // Show error to user instead of silently failing
          setError(setErr.response?.data?.message || setErr.message || 'Payment method was added but failed to set as default. Please try again.');
          return;
        }
        
        setSuccess('Payment method updated successfully!');
        if (onSuccess) {
          console.log('PaymentMethodForm: onSuccess called');
          onSuccess();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update payment method.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
      <CardElementAny options={{ hidePostalCode: true }} />
      <Box mt={2}>
        <Button type="submit" variant="contained" color="primary" disabled={!stripe || loading}>
          {loading ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </Box>
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </form>
  );
};

// Account Credits Top-up Form
const AccountTopupForm: React.FC<{ 
  agencyId: string; 
  savedPaymentMethod?: { id: string; brand?: string; last4?: string } | null;
  onSuccess?: () => void;
  onClose?: () => void;
}> = ({ agencyId, savedPaymentMethod, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amountDollars, setAmountDollars] = useState<string>('50');
  const [useNewCard, setUseNewCard] = useState(false);

  React.useEffect(() => {
    setUseNewCard(false);
    setSuccess(null);
    setError(null);
  }, [savedPaymentMethod]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      if (!stripe) throw new Error('Stripe not loaded');
      const dollars = Number(amountDollars);
      if (Number.isNaN(dollars) || dollars <= 0) throw new Error('Enter a valid amount');
      const amountCents = Math.round(dollars * 100);

      const { data } = await api.post(`/billing/${agencyId}/campaign-topup-intent`, { 
        amountCents,
        paymentMethodId: useNewCard ? undefined : savedPaymentMethod?.id
      });
      const clientSecret = data.clientSecret;
      const paymentMethodIdFromBackend = data.paymentMethodId;

      let result;
      if (useNewCard || !savedPaymentMethod) {
        if (!elements) throw new Error('Stripe elements not loaded');
        const cardElement = elements.getElement(CardElement as any) as any;
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement as any,
          },
        });
      } else if (paymentMethodIdFromBackend) {
        result = await stripe.confirmCardPayment(clientSecret);
      } else {
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: savedPaymentMethod.id,
        });
      }

      if (result.error) {
        setError(result.error.message || 'Payment failed.');
      } else {
        try {
          const paymentIntentId = (result as any)?.paymentIntent?.id;
          if (paymentIntentId) {
            await api.post(`/billing/${agencyId}/campaign-topup-confirm`, { paymentIntentId });
          }
        } catch (confirmErr) {
          console.warn('Top-up confirm failed (non-fatal):', confirmErr);
        }
        setSuccess('Top-up successful! Credits will appear shortly.');
        if (onSuccess) {
          await onSuccess();
          setTimeout(() => {
            if (onClose) onClose();
          }, 1500);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to top up credits.');
    } finally {
      setLoading(false);
    }
  };

  const hasSavedPaymentMethod = savedPaymentMethod && !useNewCard;

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 520 }}>
      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <TextField
          label="Top-up amount (AUD)"
          type="number"
          value={amountDollars}
          onChange={(e) => setAmountDollars(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          sx={{ width: 220 }}
        />
        <Typography variant="body2" color="text.secondary">
          You will be charged immediately.
        </Typography>
      </Box>
      
      {hasSavedPaymentMethod ? (
        <Box mb={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Using saved payment method: {savedPaymentMethod.brand?.toUpperCase()} **** {savedPaymentMethod.last4}
          </Alert>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => setUseNewCard(true)}
            sx={{ mb: 2 }}
          >
            Use a different card
          </Button>
        </Box>
      ) : (
        <Box mb={2}>
          <CardElementAny options={{ hidePostalCode: true }} />
        </Box>
      )}
      
      <Box mt={2}>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          disabled={(!stripe || loading) || (!useNewCard && !hasSavedPaymentMethod && !elements)}
        >
          {loading ? 'Processing...' : 'Top up credits'}
        </Button>
      </Box>
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </form>
  );
};

const getStorageLabel = (plan: Plan) => {
  if (plan.storageLimit === 0 && plan.metadata?.storageType === 'custom') {
    return 'Customised';
  }
  if (typeof plan.storageLimit === 'number' && plan.storageLimit > 0) {
    return `${plan.storageLimit}GB`;
  }
  return 'Unlimited';
};

const resolveUserLimitLabel = (plan: Plan) => {
  const seatLimit =
    typeof (plan as any).seatLimit === 'number' ? (plan as any).seatLimit : null;
  if (seatLimit && seatLimit > 0) {
    return seatLimit;
  }

  const metadataLimit = Number(
    plan.metadata?.maxUsers ??
      plan.metadata?.userLimit ??
      plan.metadata?.users
  );
  if (!Number.isNaN(metadataLimit) && metadataLimit > 0) {
    return metadataLimit;
  }

  if (['free', 'agency free', 'individual', 'elite individual'].includes(plan.name?.toLowerCase() ?? '')) return 1;

  // Avoid “Unlimited” wording here because these plans are still charged per user.
  return 'No seat cap';
};

const ManageSubscription: React.FC = () => {
  const { agency, refreshAuth } = useAuth();
  const { refreshFeatures } = useFeatures();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<AgencySubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [planUpdating, setPlanUpdating] = useState(false);
  const [planUpdateSuccess, setPlanUpdateSuccess] = useState<string | null>(null);
  const [planUpdateError, setPlanUpdateError] = useState<string | null>(null);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [addonSubscriptions, setAddonSubscriptions] = useState<any[]>([]);
  const [addonLoading, setAddonLoading] = useState(false);
  const [addonCancelingId, setAddonCancelingId] = useState<string | null>(null);
  const [showAddonCancelDialog, setShowAddonCancelDialog] = useState(false);
  const [pendingCancelAddonId, setPendingCancelAddonId] = useState<string | null>(null);
  const [showAddonConfirmDialog, setShowAddonConfirmDialog] = useState(false);
  const [selectedAddonPlan, setSelectedAddonPlan] = useState<Plan | null>(null);
  const [addonQuantity, setAddonQuantity] = useState<number>(1);
  const [addonBillingCycle, setAddonBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [showPlanConfirmDialog, setShowPlanConfirmDialog] = useState(false);
  const [selectedPlanForConfirm, setSelectedPlanForConfirm] = useState<Plan | null>(null);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(true); // TODO: set to false and fetch real status
  const [paymentMethodDetails, setPaymentMethodDetails] = useState<any>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [pendingSubscribe, setPendingSubscribe] = useState(false);
  const [dialogSubscribing, setDialogSubscribing] = useState(false);
  const [dialogSubscribeError, setDialogSubscribeError] = useState<string | null>(null);
  const [billingShowCount, setBillingShowCount] = useState(5);
  const [planBillingSelections, setPlanBillingSelections] = useState<Record<string, 'monthly' | 'annual'>>({});
  const [accountBalanceCents, setAccountBalanceCents] = useState<number>(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const currentPlanId =
    subscription?.items?.[0]?.priceId ||
    subscription?.items?.[0]?.productId ||
    null;

  const subscriptionInTrial = useMemo(() => isSubscriptionInTrial(subscription), [subscription]);

  const isPlanPending = (plan?: Plan | null) => plan?.metadata?.pending === 'true';

  const getDefaultBillingCycleForPlan = (plan?: Plan | null): 'monthly' | 'annual' => {
    if (!plan) return 'monthly';
    if (plan.billingPeriod === 'annual' || plan.billingPeriod === 'hybrid') return 'annual';
    return 'monthly';
  };

  const getPlanBillingCycle = (plan?: Plan | null): 'monthly' | 'annual' => {
    if (!plan) return 'monthly';
    return planBillingSelections[plan._id] || getDefaultBillingCycleForPlan(plan);
  };

  useEffect(() => {
    if (!plans.length) return;
    setPlanBillingSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      plans.forEach((plan) => {
        if (!next[plan._id]) {
          next[plan._id] = getDefaultBillingCycleForPlan(plan);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [plans]);

  const handleSelectPlan = (plan: Plan, isDisabled: boolean) => {
    if (isDisabled) return;
    setSelectedPlanId(plan._id);
    setPlanBillingSelections((prev) => {
      if (prev[plan._id]) return prev;
      return { ...prev, [plan._id]: getDefaultBillingCycleForPlan(plan) };
    });
  };

  useEffect(() => {
    if (agency?.slug) {
      fetchData();
      fetchPaymentMethod();
      loadAddonSubscriptions();
      fetchAccountBalance();
    }
  }, [agency?.slug]);

  // Fetch billing history on page load
  useEffect(() => {
    if (agency?.slug) {
      fetchBillingHistory();
    }
  }, [agency?.slug]);

  useEffect(() => {
    setBillingShowCount(5); // Reset to 5 whenever billingHistory changes
  }, [billingHistory]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, subscriptionRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get(`/billing/${agency?.slug}/subscriptions`)
      ]);
      
      setPlans(plansRes.data);
      setSubscription(subscriptionRes.data.subscription);
      
      if (subscriptionRes.data.subscription) {
        setSelectedPlanId(subscriptionRes.data.subscription.items[0]?.productId || null);
      }
    } catch (err) {
      setError('Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async () => {
    if (!agency?.slug) return;
    
    setBillingLoading(true);
    setBillingError(null);
    try {
      const res = await api.get(`/billing/${agency.slug}/billing-history`);
      setBillingHistory(res.data);
    } catch (err) {
      setBillingError('Failed to fetch billing history');
    } finally {
      setBillingLoading(false);
    }
  };

  const fetchPaymentMethod = async () => {
    if (!agency?.slug) return;
    
    try {
      const res = await api.get(`/billing/${agency.slug}/payment-method`);
      setHasPaymentMethod(res.data.hasPaymentMethod);
      setPaymentMethodDetails(res.data.paymentMethod || null);
    } catch (err) {
      setHasPaymentMethod(false);
      setPaymentMethodDetails(null);
    }
  };

  const fetchAccountBalance = async () => {
    if (!agency?.slug) return;
    try {
      setBalanceLoading(true);
      setBalanceError(null);
      const res = await api.get(`/agencies/${agency.slug}/campaign-credits/balance`);
      setAccountBalanceCents(res.data.balanceCents || 0);
    } catch (e: any) {
      setBalanceError(e?.response?.data?.error || e?.message || 'Failed to fetch account balance');
    } finally {
      setBalanceLoading(false);
    }
  };

  const handlePlanUpdate = async () => {
    if (!selectedPlanId || !agency?.slug) return;
    
    // Find the selected plan
    const selectedPlan = plans.find(p => p._id === selectedPlanId);
    if (!selectedPlan) return;
    
    // Show confirmation dialog
    setSelectedPlanForConfirm(selectedPlan);
    setShowPlanConfirmDialog(true);
  };

  const handleConfirmPlanUpdate = async () => {
    if (!hasPaymentMethod) {
      setShowPaymentDialog(true);
      setPendingSubscribe(true);
      setShowPlanConfirmDialog(false);
      return;
    }
    setShowPlanConfirmDialog(false);
    await doSubscribe();
  };

  const doSubscribe = async () => {
    setPlanUpdating(true);
    setDialogSubscribing(true);
    setDialogSubscribeError(null);
    setPlanUpdateSuccess(null);
    setPlanUpdateError(null);
    try {
      console.log('doSubscribe called');
      if (!agency?.slug || !selectedPlanId) return;
      const targetPlan = plans.find(p => p._id === selectedPlanId);
      if (!targetPlan) return;
      const billingCycleForRequest = getPlanBillingCycle(targetPlan);
      let response;
      
      // For Free plans, treat as creating new subscription instead of updating
      if (subscription && !isCurrentPlanFree()) {
        response = await api.put(`/billing/${agency.slug}/subscriptions`, {
          planId: selectedPlanId,
          billingCycle: billingCycleForRequest
        });
      } else {
        response = await api.post(`/billing/${agency.slug}/subscriptions`, {
          planId: selectedPlanId,
          billingCycle: billingCycleForRequest
        });
      }

      const clientSecret = response?.data?.subscription?.clientSecret;
      if (clientSecret) {
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe is not available');
        }
        const confirmOptions =
          paymentMethodDetails?.id
            ? { payment_method: paymentMethodDetails.id }
            : undefined;
        const paymentResult = await stripe.confirmCardPayment(
          clientSecret,
          confirmOptions
        );
        if (paymentResult.error) {
          throw new Error(paymentResult.error.message || 'Payment confirmation failed');
        }
        const paymentStatus = (paymentResult as any)?.paymentIntent?.status;
        if (paymentStatus !== 'succeeded') {
          throw new Error(`Payment not completed (status: ${paymentStatus || 'unknown'})`);
        }
      }

      await fetchData();
      // Refresh auth state to update agency subscription data
      await refreshAuth();
      setPlanUpdateSuccess(
        subscription && !isCurrentPlanFree()
          ? 'Subscription updated successfully!'
          : 'Subscription created successfully!'
      );
      setShowPaymentDialog(false);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to update subscription';
      setPlanUpdateError(errorMessage);
      setDialogSubscribeError(errorMessage);
      console.error('doSubscribe error:', err);
    } finally {
      setPlanUpdating(false);
      setDialogSubscribing(false);
      setPendingSubscribe(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!agency?.slug) return;
    
    setCancelLoading(true);
    try {
      // Determine cancellation behavior:
      // - If in trial: cancel immediately (no point in waiting)
      // - If paid: cancel at end of billing period (user paid for the period)
      const isInTrial = isSubscriptionInTrial(subscription);
      const cancelAtPeriodEnd = !isInTrial; // Only wait for period end if not in trial
      
      console.log(`Subscription status: ${subscription?.status}, isInTrial: ${isInTrial}, cancelAtPeriodEnd: ${cancelAtPeriodEnd}`);
      
      await api.delete(`/billing/${agency.slug}/subscriptions`, {
        data: { cancelAtPeriodEnd }
      });
      
      const successMessage = isInTrial 
        ? 'Subscription canceled immediately (trial period)'
        : 'Subscription will be canceled at the end of the current billing period';
      
      setPlanUpdateSuccess(successMessage);
      setShowCancelDialog(false);
      await fetchData();
      // Refresh auth state to update agency subscription data
      await refreshAuth();
    } catch (err: any) {
      setPlanUpdateError(err.response?.data?.message || 'Failed to cancel subscription');
      setShowCancelDialog(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!agency?.slug) return;
    
    setPlanUpdating(true);
    try {
      await api.patch(`/billing/${agency.slug}/subscriptions/reactivate`);
      setPlanUpdateSuccess('Subscription reactivated successfully!');
      await fetchData();
      // Refresh auth state to update agency subscription data
      await refreshAuth();
    } catch (err: any) {
      setPlanUpdateError(err.response?.data?.message || 'Failed to reactivate subscription');
    } finally {
      setPlanUpdating(false);
    }
  };

  const formatDate = (timestamp: number | undefined | null) => {
    if (!timestamp || isNaN(timestamp)) return '-';
    const date = new Date(timestamp * 1000);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateFromDate = (date: Date | string | undefined | null) => {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'info';
      case 'canceled': return 'error';
      case 'past_due': return 'warning';
      default: return 'default';
    }
  };

  // Helper to get current plan name
  const getCurrentPlanName = () => {
    if (!subscription) return '';
    
    // First try to get planName from the items array (new format)
    if (subscription.items && subscription.items[0]?.planName) {
      return subscription.items[0].planName;
    }
    
    // Fallback to old logic for Stripe-based subscriptions
    if (!plans.length) return '';
    const currentProductId = subscription.items[0]?.productId;
    const plan = plans.find(p => p.stripeProductId === currentProductId);
    return plan ? plan.name : '';
  };

  // Helper to render current period or next billing/trial end
  const renderCurrentPeriod = () => {
    if (!subscription) return '-';
    const start = subscription.currentPeriodStart;
    const end = subscription.currentPeriodEnd;
    if (start && end && !isNaN(start) && !isNaN(end)) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    if (subscription.trialEnd && !isNaN(subscription.trialEnd)) {
      return `Trial ends: ${formatDate(subscription.trialEnd)}`;
    }
    // Optionally, show next billing date if available (could be currentPeriodEnd)
    if (end && !isNaN(end)) {
      return `Next billing: ${formatDate(end)}`;
    }
    return '-';
  };

  // Helper to check if current plan is Free
  const isCurrentPlanFree = () => {
    if (!subscription) return false;
    
    // First check if planName is "Free" (new format)
    if (subscription.items && ['Free', 'Agency Free'].includes(subscription.items[0]?.planName ?? '')) {
      return true;
    }
    
    // Fallback to old logic for Stripe-based subscriptions
    if (!plans.length) return false;
    const currentProductId = subscription.items[0]?.productId;
    const plan = plans.find(p => p.stripeProductId === currentProductId);
    return plan ? plan.monthlyFee === 0 : false;
  };

  // Individual plan cannot switch to Agency plan
  const isCurrentPlanIndividual = () => {
    if (!subscription) return false;
    if (subscription.items && ['Individual', 'Elite Individual'].includes(subscription.items[0]?.planName ?? '')) return true;
    if (!plans.length) return false;
    const currentProductId = subscription.items[0]?.productId;
    const plan = plans.find(p => p.stripeProductId === currentProductId);
    return plan?.metadata?.upgradeable === 'false';
  };

  // Individual and agency plans are not convertible - show only plans of the same type
  const isIndividualPlan = (plan: Plan) =>
    ['Individual', 'Elite Individual'].includes(plan.name ?? '');
  const isAgencyPlan = (plan: Plan) => !isIndividualPlan(plan);

  const isStorageAddon = (plan: Plan | null | undefined) =>
    !!plan && (plan.metadata?.addonType === 'storage' || plan.name === 'Storage Add-on');

  const isDigitalAssistantAddon = (plan: Plan | null | undefined) =>
    !!plan &&
    (plan.metadata?.featureKey === 'property_assistant' ||
      plan.metadata?.addonType === 'feature' ||
      /digital assistant/i.test(plan.name || ''));

  const isSeatBasedAddon = (plan: Plan | null | undefined) =>
    !!plan &&
    (!!plan.seatsBased ||
      plan.metadata?.seatsBased === 'true' ||
      isDigitalAssistantAddon(plan));

  // Show confirmation dialog for adding an add-on
  const handleAddStorageAddonClick = (plan: Plan) => {
    setSelectedAddonPlan(plan);
    const defaultSeats = Math.max(1, Number((agency as any)?.seatsUsed) || 1);
    setAddonQuantity(isSeatBasedAddon(plan) ? defaultSeats : 1);
    setAddonBillingCycle('monthly');
    setShowAddonConfirmDialog(true);
  };

  // Handle confirming and adding add-on subscription
  const handleConfirmAddStorageAddon = async () => {
    if (!agency?.slug || !selectedAddonPlan) {
      setError('Agency not found');
      return;
    }

    setAddonLoading(true);
    setError(null);
    setShowAddonConfirmDialog(false);

    try {
      await api.post(`/billing/${agency.slug}/addons`, {
        planId: selectedAddonPlan._id,
        planName: selectedAddonPlan.name,
        stripeProductId: selectedAddonPlan.stripeProductId,
        stripeMonthlyPriceId: selectedAddonPlan.stripeMonthlyPriceId,
        stripeAnnualPriceId: selectedAddonPlan.stripeAnnualPriceId,
        quantity:
          isStorageAddon(selectedAddonPlan) || isSeatBasedAddon(selectedAddonPlan)
            ? addonQuantity
            : 1,
        billingCycle: addonBillingCycle,
      });

      if (isStorageAddon(selectedAddonPlan)) {
        const totalAdded = 50 * addonQuantity;
        setPlanUpdateSuccess(
          `Successfully added ${selectedAddonPlan.name}! Your storage limit has been increased by ${totalAdded}GB.`
        );
      } else if ((selectedAddonPlan.trialDays || 0) > 0) {
        setPlanUpdateSuccess(
          `Started your ${selectedAddonPlan.trialDays}-day free trial of ${selectedAddonPlan.name} for ${addonQuantity} seat${addonQuantity === 1 ? '' : 's'}. The Digital Assistant is now available.`
        );
      } else {
        setPlanUpdateSuccess(
          `Successfully subscribed to ${selectedAddonPlan.name} for ${addonQuantity} seat${addonQuantity === 1 ? '' : 's'}!`
        );
      }
      
      // Refresh add-on subscriptions
      await loadAddonSubscriptions();
      if (isDigitalAssistantAddon(selectedAddonPlan)) {
        await refreshFeatures();
      }
      
    } catch (err: any) {
      console.error('Error adding add-on:', err);
      
      // Handle specific Stripe payment method error
      if (err.response?.data?.message?.includes('payment method') || 
          err.response?.data?.message?.includes('payment source')) {
        setError('Please add a payment method before subscribing to add-ons. Click the "Add Payment Method" button below to add your card.');
      } else {
        setError(err.response?.data?.message || 'Failed to add add-on');
      }
    } finally {
      setAddonLoading(false);
      setSelectedAddonPlan(null);
    }
  };

  // Load add-on subscriptions
  const loadAddonSubscriptions = async () => {
    if (!agency?.slug) return;

    try {
      const response = await api.get(`/billing/${agency.slug}/addons`);
      setAddonSubscriptions(response.data.addons || []);
    } catch (err: any) {
      console.error('Error loading add-on subscriptions:', err);
      // Don't set error state for this, just log it
    }
  };

  const openCancelAddonDialog = (addonId: string) => {
    setPendingCancelAddonId(addonId);
    setShowAddonCancelDialog(true);
  };

  const handleConfirmCancelAddon = async () => {
    if (!agency?.slug || !pendingCancelAddonId) return;
    setAddonCancelingId(pendingCancelAddonId);
    try {
      await api.delete(`/billing/${agency.slug}/addons/${pendingCancelAddonId}`);
      await loadAddonSubscriptions();
      // Entitlement may still apply until period end (canceling); refresh so UI stays in sync
      await refreshFeatures();
      setShowAddonCancelDialog(false);
      setPendingCancelAddonId(null);
    } catch (err) {
      console.error('Error canceling add-on:', err);
    } finally {
      setAddonCancelingId(null);
    }
  };

  const handleCloseCancelAddonDialog = () => {
    setShowAddonCancelDialog(false);
    setPendingCancelAddonId(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Management
      </Typography>
      

      {/* Account Balance */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Account Balance
            </Typography>
            <Box display="flex" gap={1}>
              <Button 
                onClick={fetchAccountBalance} 
                disabled={balanceLoading}
                size="small"
              >
                {balanceLoading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button 
                variant="contained" 
                onClick={() => setShowTopupDialog(true)}
                size="small"
              >
                Top up
              </Button>
            </Box>
          </Box>
          {balanceError && <Alert severity="error" sx={{ mb: 2 }}>{balanceError}</Alert>}
          <Typography variant="body1">
            Balance: <strong>${(accountBalanceCents / 100).toFixed(2)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Credits are used for extra costs including market campaigns, bulk emails, SMS, and other premium features.
          </Typography>
        </CardContent>
      </Card>

      {/* Current Subscription Status */}
      {subscription && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Subscription
            </Typography>
            {getCurrentPlanName() && (
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                {getCurrentPlanName()}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography component="div" variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  Status: <Chip 
                    label={isCurrentPlanFree() ? 'active' : subscription.status} 
                    color={getStatusColor(isCurrentPlanFree() ? 'active' : subscription.status) as any}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {!isCurrentPlanFree() && (
                  <Typography variant="body2" color="text.secondary">
                    Current Period: {renderCurrentPeriod()}
                  </Typography>
                )}
                {subscription.cancelAtPeriodEnd && (
                  <Typography variant="body2" color="warning.main">
                    ⚠️ Subscription will be canceled at the end of the current period
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={1}>
                  {subscription.cancelAtPeriodEnd && (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleReactivateSubscription}
                      disabled={planUpdating}
                    >
                      Reactivate
                    </Button>
                  )}
                  {!subscription.cancelAtPeriodEnd && !isCurrentPlanFree() && (
                    <Button 
                      variant="outlined" 
                      color="error"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

    {/* Add-on Cancel Confirmation Dialog */}
    <Dialog
      open={showAddonCancelDialog}
      onClose={handleCloseCancelAddonDialog}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Cancel Add-on?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This add-on will remain active until the end of the current billing period. Do you want to proceed?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseCancelAddonDialog}>Keep Add-on</Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={handleConfirmCancelAddon}
          disabled={!!addonCancelingId}
        >
          {addonCancelingId ? 'Canceling...' : 'Cancel Add-on'}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Plan Selection */}
      {/* Base Plans Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {subscription ? 'Change Base Plan' : 'Select Base Plan'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose your main subscription plan. You can add storage add-ons separately below.
          </Typography>
          {isCurrentPlanIndividual() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Individual plan cannot switch to Agency plan. Cancel your subscription to change plans.
            </Alert>
          )}
          <Grid container spacing={3}>
            {plans
              .filter(plan => {
                if (!plan.isActive || plan.metadata?.planType === 'addon' || isPlanPending(plan) || /^WE Openly Standalone/i.test(plan.name || '')) {
                  return false;
                }
                // Individual and agency plans are not convertible - hide the other type
                if (isCurrentPlanIndividual()) {
                  return isIndividualPlan(plan); // On Individual: show only Individual plan
                }
                return isAgencyPlan(plan); // On agency plan: hide Elite Individual
              })
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((plan) => {
                const isPendingPlan = isPlanPending(plan);
                const isContactSales = plan.metadata?.contactSales === 'true';
                const isFreePlanCard = ['free', 'agency free'].includes(plan.name?.toLowerCase() ?? '');
                const isFreeSelectionBlocked = Boolean(isFreePlanCard && subscription && !isCurrentPlanFree());
                const isIndividualPlanLocked = isCurrentPlanIndividual();
                const isCurrentPlanCard = subscription?.items?.[0]?.productId === plan.stripeProductId;
                const isOtherPlanBlockedForIndividual = isIndividualPlanLocked && !isCurrentPlanCard;
                const isDisabled = isPendingPlan || isContactSales || isFreeSelectionBlocked || isOtherPlanBlockedForIndividual;
                const planBillingCycle = getPlanBillingCycle(plan);
                const isAnnualBilling = planBillingCycle === 'annual';
                const showBillingToggle = plan.billingPeriod === 'both' && !isContactSales;
                const annualAmount = plan.annualFee ?? (plan.monthlyFee * 12);
                const displayPriceValue = plan.monthlyFee === 0
                  ? 'Free'
                  : `$${isAnnualBilling ? annualAmount : plan.monthlyFee}`;
                const priceSuffix = plan.monthlyFee === 0
                  ? ''
                  : isAnnualBilling
                    ? '/user/year'
                    : '/user/month';
                const contactSalesPath = agency?.slug
                  ? `/agencies/${agency.slug}/contact-sales`
                  : '/contact-sales';
                const isSelected = selectedPlanId === plan._id;
                const showInlineUpdateButton =
                  isSelected && !isContactSales && !isDisabled && !isCurrentPlanCard;
                return (
                <Grid item xs={12} md={4} key={plan._id}>
                  <Card
                    sx={{
                      position: 'relative',
                      height: '100%',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.7 : 1,
                      border: selectedPlanId === plan._id ? '2px solid' : '1px solid',
                      borderColor: selectedPlanId === plan._id 
                        ? (['Free', 'Agency Free'].includes(plan.name ?? '') ? 'success.main' : ['Individual', 'Elite Individual'].includes(plan.name ?? '') ? '#7c3aed' : 'primary.main')
                        : (['Free', 'Agency Free'].includes(plan.name ?? '') ? '#e8f5e8' : ['Individual', 'Elite Individual'].includes(plan.name ?? '') ? '#ede9fe' : 'divider'),
                      '&:hover': {
                        boxShadow: isDisabled ? 'none' : 4,
                      },
                    }}
                    onClick={() => handleSelectPlan(plan, isDisabled)}
                    aria-disabled={isDisabled}
                  >
                    {isPendingPlan && (
                      <Chip
                        label="Coming Soon"
                        color="warning"
                        size="small"
                        sx={{ position: 'absolute', top: 16, right: 16 }}
                      />
                    )}
                    <CardHeader
                      title={plan.name}
                      sx={{
                        background: ['Free', 'Agency Free'].includes(plan.name ?? '')
                          ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                          : ['Individual', 'Elite Individual'].includes(plan.name ?? '')
                            ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
                            : 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                        color: 'white',
                        textAlign: 'center',
                        pb: 1,
                        '& .MuiCardHeader-title': {
                          fontSize: '24px',
                          fontWeight: 700,
                          color: 'white'
                        }
                      }}
                    >
                      {!isContactSales && (
                        <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                          <Radio 
                            value={plan._id} 
                            checked={isSelected}
                            disabled={isDisabled}
                            sx={{ color: 'white', mr: 1 }}
                          />
                        </Box>
                      )}
                    </CardHeader>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Billing Cycle Toggle */}
                      {showBillingToggle && (
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={planBillingCycle}
                          onChange={(event, value) => {
                            if (!value) return;
                            event.stopPropagation();
                            setPlanBillingSelections((prev) => ({ ...prev, [plan._id]: value }));
                          }}
                          sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                        >
                          <ToggleButton value="monthly" disabled={isDisabled}>
                            Monthly
                          </ToggleButton>
                          <ToggleButton value="annual" disabled={isDisabled || (!plan.annualFee && !plan.stripeAnnualPriceId)}>
                            Annual
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}

                      {/* Pricing Display */}
                      {showInlineUpdateButton && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handlePlanUpdate();
                            }}
                          >
                            Update Plan
                          </Button>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                        {isContactSales ? (
                          <Typography component="div" variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Contact Sales
                          </Typography>
                        ) : (
                          <>
                            <Typography component="div" variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {displayPriceValue}
                            </Typography>
                            {priceSuffix && (
                              <Typography component="div" variant="h6" sx={{ ml: 1, opacity: 0.7, color: 'text.secondary' }}>
                                {priceSuffix}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                      {!isContactSales && plan.trialDays > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'center',
                            mb: 2,
                            color: (plan.trialEligible ?? true) ? 'success.main' : 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          {(plan.trialEligible ?? true)
                            ? `${plan.trialDays}-day free trial`
                            : 'Trial already used'}
                        </Typography>
                      )}
                      
                      {/* Key Metrics */}
                      <Grid container spacing={2} mb={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Users
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {resolveUserLimitLabel(plan)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Storage
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {getStorageLabel(plan)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Support
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {plan.supportLevel === 'community' ? 'Community' :
                             plan.supportLevel === 'business_hours' ? 'Business Hours' :
                             '24/7 Premium'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* Features List */}
                      <List dense>
                        {plan.features.map((feature, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon color="success" sx={{ fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature.replace('✅ ', '')}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.primary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {isFreeSelectionBlocked && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Cancel your current subscription to move back to the Free plan.
                        </Alert>
                      )}
                    </CardContent>
                    {isContactSales && (
                      <Box sx={{ p: 3, pt: 0, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`${contactSalesPath}?plan=${encodeURIComponent(plan.name)}`);
                          }}
                        >
                          Contact Sales
                        </Button>
                      </Box>
                    )}
                  </Card>
                </Grid>
                );
              })}
          </Grid>

        </CardContent>
      </Card>

      {/* Add-ons Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add-ons
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add optional add-ons to your current plan.
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => setError(null)}
              action={
                error.includes('payment method') ? (
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => {
                      setError(null);
                      setShowPaymentDialog(true);
                    }}
                  >
                    Add Payment Method
                  </Button>
                ) : null
              }
            >
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {plans
              .filter(plan => {
                if (!plan.isActive || plan.metadata?.planType !== 'addon') return false;
                if (isPlanPending(plan)) return false;
                if (/^WE Openly Standalone/i.test(plan.name || '')) return false;
                // WE Openly Add-on removed; Storage + Digital Assistant add-ons are offered
                if (plan.name === 'WE Openly Add-on' || plan.metadata?.addonType === 'mobile') return false;
                
                // Check if current plan is excluded from this add-on
                const excludedPlans = plan.metadata?.excludedPlans;
                if (excludedPlans) {
                  const currentPlanName = getCurrentPlanName() || 'Free';
                  const excludedPlansList = typeof excludedPlans === 'string'
                    ? excludedPlans.split(',').map(p => p.trim())
                    : Array.isArray(excludedPlans)
                      ? excludedPlans
                      : [excludedPlans];
                  
                  if (excludedPlansList.includes(currentPlanName)) {
                    return false; // Hide this add-on for current plan
                  }
                }
                
                return true;
              })
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((plan) => (
                <Grid item xs={12} md={6} key={plan._id}>
                  {(() => {
                    const storage = isStorageAddon(plan);
                    const assistant = isDigitalAssistantAddon(plan);
                    const alreadySubscribed = addonSubscriptions.some(
                      (a) =>
                        a.planId === plan._id ||
                        a.planName === plan.name ||
                        (assistant &&
                          (a.metadata?.featureKey === 'property_assistant' ||
                            /digital assistant/i.test(a.planName || '')))
                    );
                    const ctaLabel = storage
                      ? 'Add Storage'
                      : assistant
                        ? alreadySubscribed
                          ? 'Subscribed'
                          : 'Add Digital Assistant'
                        : 'Add Add-on';
                    const summaryTitle = storage
                      ? '+50GB Storage'
                      : assistant
                        ? 'AI Property Assistant'
                        : plan.name;
                    const summarySubtitle = storage
                      ? 'Additional storage for your agency'
                      : assistant
                        ? 'Seat-based · web chatbot + mobile talking avatar'
                        : 'Optional add-on for your agency';
                    const headerGradient = storage
                      ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                      : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                    const accent = storage ? '#4caf50' : '#2563eb';
                    return (
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardHeader
                      title={plan.name}
                      sx={{
                        background: headerGradient,
                        color: 'white',
                        textAlign: 'center',
                        pb: 1,
                        '& .MuiCardHeader-title': {
                          fontSize: '20px',
                          fontWeight: 700,
                          color: 'white'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                        <Radio 
                          value={plan._id} 
                          checked={false}
                          sx={{ color: 'white', mr: 1 }}
                        />
                      </Box>
                    </CardHeader>
                    
                    <CardContent>
                      {/* Pricing Display */}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap', mb: 1 }}>
                        <Typography component="div" variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          ${plan.monthlyFee}
                        </Typography>
                        <Typography component="div" variant="h6" sx={{ ml: 1, opacity: 0.7, color: 'text.secondary' }}>
                          {assistant || plan.seatsBased ? '/user/month' : '/tenancy/month'}
                        </Typography>
                      </Box>
                      {!!plan.annualFee && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                          or ${plan.annualFee}
                          {assistant || plan.seatsBased ? '/user/year' : '/tenancy/year'}
                        </Typography>
                      )}
                      {assistant && (plan.trialDays || 0) > 0 && (
                        <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'success.main', fontWeight: 600 }}>
                          {plan.trialDays}-day free trial
                        </Typography>
                      )}
                      
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {summaryTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {summarySubtitle}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        {plan.features.map((feature, index) => (
                          <Box key={index} display="flex" alignItems="center" mb={1}>
                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2">
                              {feature.replace('✅ ', '')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Button
                          variant="outlined"
                          size="large"
                          fullWidth
                          disabled={addonLoading || alreadySubscribed}
                          onClick={() => handleAddStorageAddonClick(plan)}
                          sx={{
                            borderColor: accent,
                            color: accent,
                            '&:hover': {
                              backgroundColor: accent,
                              color: 'white',
                            },
                          }}
                        >
                          {addonLoading ? 'Adding...' : ctaLabel}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Requires payment method
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                    );
                  })()}
                </Grid>
              ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Current Add-on Subscriptions */}
      {addonSubscriptions.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Add-ons
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your active add-on subscriptions.
            </Typography>
            
            <Grid container spacing={2}>
              {addonSubscriptions.map((addon, index) => (
                <Grid item xs={12} md={6} key={addon._id || addon.id || index}>
                  <Card
                    sx={{
                      border: '1px solid',
                      borderColor: 'success.main',
                      backgroundColor: '#f8fff8',
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          {(() => {
                            const planName = (addon.planName || '').toString();
                            const isOpenlyAddon = planName.toLowerCase().includes('openly');
                            const isAssistant =
                              addon.addonType === 'feature' ||
                              addon.metadata?.featureKey === 'property_assistant' ||
                              /digital assistant/i.test(planName);
                            const isStorage =
                              !isOpenlyAddon &&
                              !isAssistant &&
                              (addon.addonType === 'storage' || (addon.storageAmountGB || 0) > 0);
                            const seats = Number(addon.metadata?.seats || addon.metadata?.quantity || 1);
                            const qtySuffix = isStorage
                              ? (() => {
                                  const qty = Math.max(
                                    1,
                                    Math.round(((addon.storageAmountGB || 50) as number) / 50)
                                  );
                                  return qty > 1 ? ` x ${qty}` : '';
                                })()
                              : isAssistant && seats > 0
                                ? ` · ${seats} seat${seats === 1 ? '' : 's'}`
                                : '';
                            const cycle = addon.metadata?.billingCycle === 'annual' ? 'year' : 'month';
                            const unitPrice =
                              cycle === 'year'
                                ? addon.annualPrice || addon.metadata?.annualFee
                                : addon.monthlyPrice || addon.metadata?.monthlyFee;
                            const totalPrice =
                              unitPrice != null && isAssistant
                                ? Number(unitPrice) * Math.max(1, seats)
                                : unitPrice;

                            return (
                              <>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {addon.planName || 'Add-on'}
                                  {qtySuffix}
                                </Typography>
                                {isOpenlyAddon ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Premium Mobile Features
                                  </Typography>
                                ) : isAssistant ? (
                                  <Typography variant="body2" color="text.secondary">
                                    AI Property Assistant (per seat)
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    +{addon.storageAmountGB || 50}GB Storage
                                  </Typography>
                                )}
                                {totalPrice != null && (
                                  <Typography variant="body2" color="text.secondary">
                                    ${Number(totalPrice).toFixed(2)}/{cycle}
                                    {isAssistant && unitPrice != null
                                      ? ` ($${Number(unitPrice).toFixed(2)}/user)`
                                      : ''}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                          {addon.currentPeriodStart && addon.currentPeriodEnd && (
                            <Typography variant="body2" color="text.secondary">
                              Period: {formatDateFromDate(addon.currentPeriodStart)} - {formatDateFromDate(addon.currentPeriodEnd)}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={(addon.status || 'active').toUpperCase()} 
                              color={addon.status === 'canceling' ? 'warning' : 'success'} 
                              size="small"
                            />
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={addonCancelingId === (addon._id || addon.id)}
                              onClick={() => openCancelAddonDialog(addon._id || addon.id)}
                            >
                              {addonCancelingId === (addon._id || addon.id) ? 'Canceling...' : 'Cancel'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const storageAddons = addonSubscriptions.filter((a) => {
                    const name = (a.planName || '').toString().toLowerCase();
                    if (name.includes('openly') || name.includes('digital assistant')) return false;
                    if (a.addonType === 'feature') return false;
                    return (a.storageAmountGB || 0) > 0 || a.addonType === 'storage' || name.includes('storage');
                  });
                  if (storageAddons.length === 0) return null;
                  const totalStorage = storageAddons.reduce((sum, a) => sum + (a.storageAmountGB || 50), 0);
                  return `Total additional storage: ${totalStorage}GB`;
                })()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment Method */}
      {agency?.slug && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            {hasPaymentMethod && paymentMethodDetails ? (
              <Box mb={2}>
                <Typography variant="body2">
                  Card: {paymentMethodDetails.brand?.toUpperCase()} **** {paymentMethodDetails.last4}
                </Typography>
                <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setShowPaymentDialog(true)}>
                  Update Payment Method
                </Button>
              </Box>
            ) : (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No payment method added. Add a payment method to subscribe to paid plans and add-ons.
                </Typography>
                <Button variant="contained" color="primary" onClick={() => setShowPaymentDialog(true)}>
                  Add Payment Method
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {/* Payment Method Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => { setShowPaymentDialog(false); setPendingSubscribe(false); setDialogSubscribeError(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{hasPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, px: 1 }}>
            <ElementsAny stripe={stripePromise}>
              <PaymentMethodForm agencyId={agency?.slug!} onSuccess={async () => {
                await fetchPaymentMethod();
                if (pendingSubscribe) {
                  setDialogSubscribing(true);
                  setDialogSubscribeError(null);
                  await doSubscribe();
                } else {
                  setShowPaymentDialog(false);
                }
              }} />
            </ElementsAny>
            {dialogSubscribing && (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Subscribing...</Typography>
              </Box>
            )}
            {dialogSubscribeError && (
              <Alert severity="error" sx={{ mt: 2 }}>{dialogSubscribeError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPaymentDialog(false); setPendingSubscribe(false); setDialogSubscribeError(null); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Billing History */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Billing History
            </Typography>
            <Button onClick={fetchBillingHistory} disabled={billingLoading}>
              {billingLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
          
          {billingError && <Alert severity="error" sx={{ mb: 2 }}>{billingError}</Alert>}
          
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingHistory.slice(0, billingShowCount).map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{new Date(invoice.date).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                  <TableCell>${invoice.amount} {invoice.currency?.toUpperCase()}</TableCell>
                  <TableCell>
                    <Chip label={invoice.status} size="small" />
                  </TableCell>
                  <TableCell>
                    {invoice.hosted_invoice_url && (
                      <Button 
                        size="small" 
                        href={invoice.hosted_invoice_url} 
                        target="_blank"
                      >
                        View Invoice
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {billingHistory.length > billingShowCount && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button onClick={() => setBillingShowCount(c => c + 5)}>
                Show More
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Success/Error Messages */}
      {planUpdateSuccess && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setPlanUpdateSuccess(null)}>
          {planUpdateSuccess}
        </Alert>
      )}
      {planUpdateError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPlanUpdateError(null)}>
          {planUpdateError}
        </Alert>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            {subscriptionInTrial ? (
              <>If you cancel now, your free trial will end immediately and you will not be able to start another trial in the future. Trials can only be used once per account.</>
            ) : (
              <>Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>Keep Subscription</Button>
          <Button 
            onClick={handleCancelSubscription} 
            color="error" 
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-on Confirmation Dialog */}
      <Dialog
        open={showAddonConfirmDialog}
        onClose={() => setShowAddonConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: isStorageAddon(selectedAddonPlan) ? '#4caf50' : '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
            <Typography sx={{ color: 'white', fontSize: '20px' }}>
              {isStorageAddon(selectedAddonPlan) ? '💾' : '🤖'}
            </Typography>
            </Box>
          <Typography variant="h6">
            {isStorageAddon(selectedAddonPlan)
              ? 'Add Storage Add-on'
              : `Add ${selectedAddonPlan?.name || 'Add-on'}`}
          </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAddonPlan && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You're about to subscribe to the <strong>{selectedAddonPlan.name}</strong>:
              </Typography>
              
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: isStorageAddon(selectedAddonPlan) ? '#4caf50' : '#2563eb',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {isStorageAddon(selectedAddonPlan)
                  ? `+${50 * addonQuantity}GB Additional Storage`
                  : `Digital Property Assistant · ${addonQuantity} seat${addonQuantity === 1 ? '' : 's'}`}
              </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
                ${
                  (
                    (addonBillingCycle === 'annual'
                      ? selectedAddonPlan.annualFee || 0
                      : selectedAddonPlan.monthlyFee || 0) *
                    (isStorageAddon(selectedAddonPlan) || isSeatBasedAddon(selectedAddonPlan)
                      ? addonQuantity
                      : 1)
                  ).toFixed(2)
                }
                  <Typography component="span" variant="h6" sx={{ ml: 1, color: '#666' }}>
                  /{addonBillingCycle === 'annual' ? 'year' : 'month'}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Billed {addonBillingCycle}
                {isSeatBasedAddon(selectedAddonPlan)
                  ? ` · $${
                      addonBillingCycle === 'annual'
                        ? selectedAddonPlan.annualFee
                        : selectedAddonPlan.monthlyFee
                    }/user`
                  : ' · Tenancy-wide'}
                {(selectedAddonPlan.trialDays || 0) > 0
                  ? ` · ${selectedAddonPlan.trialDays}-day free trial`
                  : ''}
                </Typography>
              </Box>

            {selectedAddonPlan.billingPeriod === 'both' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Billing period
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={addonBillingCycle}
                  onChange={(_, value) => {
                    if (value) setAddonBillingCycle(value);
                  }}
                >
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                  <ToggleButton
                    value="annual"
                    disabled={!selectedAddonPlan.annualFee && !selectedAddonPlan.stripeAnnualPriceId}
                  >
                    Annual
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {isStorageAddon(selectedAddonPlan) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Quantity (50GB units)
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Button variant="outlined" onClick={() => setAddonQuantity(q => Math.max(1, q - 1))}>-</Button>
                  <Typography variant="h6" sx={{ width: 40, textAlign: 'center' }}>{addonQuantity}</Typography>
                  <Button variant="outlined" onClick={() => setAddonQuantity(q => Math.min(20, q + 1))}>+</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Each unit adds 50GB. Total added: {50 * addonQuantity}GB
                </Typography>
              </Box>
            )}

            {isSeatBasedAddon(selectedAddonPlan) && !isStorageAddon(selectedAddonPlan) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Seats (agent users)
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Button variant="outlined" onClick={() => setAddonQuantity(q => Math.max(1, q - 1))}>-</Button>
                  <Typography variant="h6" sx={{ width: 40, textAlign: 'center' }}>{addonQuantity}</Typography>
                  <Button variant="outlined" onClick={() => setAddonQuantity(q => Math.min(200, q + 1))}>+</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Charged per seat. Defaults to your agency&apos;s current seat count when available.
                </Typography>
              </Box>
            )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  What you get:
                </Typography>
                {selectedAddonPlan.features.map((feature, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={0.5}>
                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      {feature.replace('✅ ', '')}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {(selectedAddonPlan.trialDays || 0) > 0
                    ? `Starts with a ${selectedAddonPlan.trialDays}-day free trial. You won’t be charged until the trial ends. You can cancel at any time.`
                    : 'This will be charged to your payment method immediately. You can cancel at any time.'}
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddonConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAddStorageAddon}
            variant="contained"
            disabled={addonLoading}
            sx={{
              backgroundColor: isStorageAddon(selectedAddonPlan) ? '#4caf50' : '#2563eb',
              '&:hover': {
                backgroundColor: isStorageAddon(selectedAddonPlan) ? '#45a049' : '#1d4ed8',
              },
            }}
          >
            {addonLoading
              ? 'Adding...'
              : (selectedAddonPlan?.trialDays || 0) > 0
                ? `Start ${selectedAddonPlan?.trialDays}-day free trial${
                    isSeatBasedAddon(selectedAddonPlan) ? ` (${addonQuantity} seat${addonQuantity === 1 ? '' : 's'})` : ''
                  }`
                : `Subscribe for $${
                    (
                      (addonBillingCycle === 'annual'
                        ? selectedAddonPlan?.annualFee || 0
                        : selectedAddonPlan?.monthlyFee || 0) *
                      (isStorageAddon(selectedAddonPlan) || isSeatBasedAddon(selectedAddonPlan)
                        ? addonQuantity
                        : 1)
                    ).toFixed(2)
                  }/${addonBillingCycle === 'annual' ? 'year' : 'month'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Confirmation Dialog */}
      <Dialog
        open={showPlanConfirmDialog}
        onClose={() => setShowPlanConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#2196f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <Typography sx={{ color: 'white', fontSize: '20px' }}>📋</Typography>
            </Box>
            <Typography variant="h6">
              {subscription ? 'Update Plan' : 'Subscribe to Plan'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPlanForConfirm && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You're about to {subscription ? 'update to' : 'subscribe to'} the <strong>{selectedPlanForConfirm.name}</strong> plan:
              </Typography>
              
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#2196f3', fontWeight: 600, mb: 1 }}>
                  {selectedPlanForConfirm.name} Plan
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
                  {(() => {
                    if (!selectedPlanForConfirm) return 'Free';
                    const billingCycle = getPlanBillingCycle(selectedPlanForConfirm);
                    if (selectedPlanForConfirm.monthlyFee === 0) return 'Free';
                    if (billingCycle === 'annual') {
                      const annualFee = selectedPlanForConfirm.annualFee ?? (selectedPlanForConfirm.monthlyFee * 12);
                      return `$${annualFee}`;
                    }
                    return `$${selectedPlanForConfirm.monthlyFee}`;
                  })()}
                  {selectedPlanForConfirm.monthlyFee > 0 && (
                    <Typography component="span" variant="h6" sx={{ ml: 1, color: '#666' }}>
                      {getPlanBillingCycle(selectedPlanForConfirm) === 'annual' ? '/user/year' : '/user/month'}
                    </Typography>
                  )}
                </Typography>
                {(selectedPlanForConfirm.trialDays ?? 0) > 0 && (
                  <Typography
                    variant="body2"
                    color={(selectedPlanForConfirm.trialEligible ?? true) ? 'success.main' : 'text.secondary'}
                    sx={{ mt: 1, fontWeight: 500 }}
                  >
                    {(selectedPlanForConfirm.trialEligible ?? true)
                      ? `${selectedPlanForConfirm.trialDays}-day free trial`
                      : 'Trial already used'}
                  </Typography>
                )}
              </Box>

              {(selectedPlanForConfirm.trialDays ?? 0) > 0 && (selectedPlanForConfirm.trialEligible ?? true) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This free trial is one-time per agency. If you use it on this plan, you won’t be eligible for trials on other plans.
                </Alert>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Plan Features:
                </Typography>
                {selectedPlanForConfirm.features.slice(0, 5).map((feature, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={0.5}>
                    <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      {feature.replace('✅ ', '')}
                    </Typography>
                  </Box>
                ))}
                {selectedPlanForConfirm.features.length > 5 && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3, fontStyle: 'italic' }}>
                    +{selectedPlanForConfirm.features.length - 5} more features
                  </Typography>
                )}
              </Box>

              {!hasPaymentMethod && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You'll need to add a payment method to complete this subscription.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlanConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmPlanUpdate}
            disabled={planUpdating}
          >
            {planUpdating ? 'Processing...' : (subscription ? 'Update Plan' : 'Subscribe')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Top-up Dialog */}
      <Dialog
        open={showTopupDialog}
        onClose={() => setShowTopupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Top up account credits</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, px: 1 }}>
            {stripePromise ? (
              <ElementsAny stripe={stripePromise}>
                <AccountTopupForm
                  agencyId={agency?.slug || ''}
                  savedPaymentMethod={paymentMethodDetails}
                  onSuccess={async () => {
                    await fetchAccountBalance();
                    setTimeout(() => {
                      fetchAccountBalance();
                    }, 2500);
                  }}
                  onClose={() => setShowTopupDialog(false)}
                />
              </ElementsAny>
            ) : (
              <Alert severity="error">Stripe is not configured. Please contact support.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTopupDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageSubscription; 